// src/context/ConfigContext.js
import React, { createContext, useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { NotificationContext } from './NotificationContext';
import { UserContext } from './UserContext';
import { apiFetch } from '../utils/apiClient'; // Import apiFetch
import { BASE_URL, IS_PRERENDERING } from '../utils/config';
import { useDevLogging } from '../hooks/useDevLogging'; // Import the logging hook

// Create the context with a default value
export const ConfigContext = createContext({
  config: null,
  isLoading: true,
  shutdownStatus: 'NORMAL', // Add default shutdown status
  setLocalShutdownStatus: () => {}, // Placeholder for the new function
  startFastPolling: () => {}, // Placeholder for fast polling
});

// Create the provider component
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shutdownStatus, setShutdownStatus] = useState('NORMAL'); // Add state for shutdown status
  const [isFastPolling, setIsFastPolling] = useState(false);

  const { addNotification } = useContext(NotificationContext);
  const { user, hasLoadedFromLocalStorage } = useContext(UserContext);

  // Ref to track if the shutdown was triggered by the client-side timer.
  const localShutdownTriggered = useRef(false);

  // --- Add Dev Logging ---
  useDevLogging('ConfigContext', {
    isLoading,
    shutdownStatus,
    isFastPolling,
    configLoaded: !!config,
    userIsAdmin: user.isAdmin,
  });

  useEffect(() => {
    // --- Prerender Gating (Step 2 from plan) ---
    if (navigator.userAgent.includes('ReactSnap')) {
      //      console.log('[Prerender] Skipping config fetch.');
      setIsLoading(false); // Ensure loading state is false for prerender
      return;
    }

    const fetchConfig = async () => {
      try {
        const response = await apiFetch(`${BASE_URL}/api/config`, {}, user.token); // Use apiFetch
        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.statusText}`);
        }
        const data = await response.json();
        setConfig(data);
      } catch (error) {
        console.error('Error fetching application configuration:', error);
        // In case of error, we can set a default config so the app doesn't break
        setConfig({
          creditPointValueILS: 1,
          creditsForReport: 10,
          creditsForFeatured1: 15,
          daysForFeatured1: 30,
          creditsForFeatured2: 22,
          daysForFeatured2: 60,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [user.token]); // Re-fetch if the user token changes (e.g., on login/logout).

  // Poll for shutdown status
  useEffect(() => {
    // --- Prerender Gating (Step 2 from plan) ---
    if (IS_PRERENDERING) {
      //      console.log('[Prerender] Skipping shutdown status polling.');
      return; // Do not set up any intervals during prerender
    }

    // Do not poll until the user state has been loaded from storage. This prevents race conditions on initial load.
    if (!hasLoadedFromLocalStorage) {
      // We don't set status to NORMAL here to avoid UI flickers if the status was already set by another means.
      return;
    }

    const fetchShutdownStatus = async () => {
      try {
        const response = await apiFetch(`${BASE_URL}/api/config/shutdown-status`, {}, user.token); // Use apiFetch
        if (response.ok) {
          const data = await response.json();
          const serverStatus = data.status || 'NORMAL';

          // If fast polling is active and the server is ready, notify the admin.
          if (isFastPolling && serverStatus === 'SHUTDOWN_PENDING') {
            addNotification('System is ready for shutdown.', 'success', null, true);
            setIsFastPolling(false); // Stop fast polling
          }

          // If the client-side timer has already triggered a shutdown,
          // and the server is still reporting NORMAL, ignore the server status
          // to prevent the UI from flickering.
          if (localShutdownTriggered.current && serverStatus === 'NORMAL') {
            return;
          }

          // Once the server status is no longer NORMAL, we can reset our local flag
          // and trust the server from now on.
          if (serverStatus !== 'NORMAL') {
            localShutdownTriggered.current = false;
          }

          setShutdownStatus(serverStatus);
        } else {
          // If the server is down or returns an error, assume normal operation for safety
          // or handle it as a potential outage. For now, let's revert to NORMAL.
          setShutdownStatus('NORMAL');
        }
      } catch (error) {
        console.error('Error fetching shutdown status:', error);
        setShutdownStatus('NORMAL'); // Revert to normal on network error
      }
    };

    // Fetch immediately on mount
    fetchShutdownStatus();

    // Adjust polling interval based on fast polling state
    const pollInterval = isFastPolling ? 3000 : 30000;
    const intervalId = setInterval(fetchShutdownStatus, pollInterval);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [isFastPolling, addNotification, hasLoadedFromLocalStorage, user.isAdmin, user.token]); // user.token re-added

  // New function to allow other components to update the status locally
  const setLocalShutdownStatus = useCallback(
    (status) => {
      if (status === 'SHUTDOWN_INITIATED') {
        localShutdownTriggered.current = true;
      }
      setShutdownStatus(status);
    },
    [] // No dependencies, so this function is stable
  );

  // Function to start fast polling, only for admins
  const startFastPolling = useCallback(() => {
    if (user.isAdmin) {
      setIsFastPolling(true);
    }
  }, [user.isAdmin]);

  const value = useMemo(() => ({ config, isLoading, shutdownStatus, setLocalShutdownStatus, startFastPolling }), [config, isLoading, shutdownStatus, setLocalShutdownStatus, startFastPolling]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};
